import { useState, type FormEvent } from 'react';
import PageLayout from '@/components/feature/PageLayout';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState<string>('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const trap = String(formData.get('website_alt') || '').trim();
    if (trap.length > 0) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const message = String(formData.get('message') || '');
    if (message.length > 500) {
      setStatus('error');
      setFormError('Votre message dépasse 500 caractères.');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus('loading');
    setFormError('');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9gg9o6lb8e5uktkn7hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMessage = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMessage);
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMessage
            ? `Votre message n'a pas pu être envoyé : ${serverMessage}`
            : "Votre message n'a pas pu être envoyé. Merci de réessayer."
        );
      }
    } catch (err) {
      setStatus('error');
      setFormError("Une erreur réseau est survenue. Merci de réessayer dans un instant.");
    }
  };

  return (
    <PageLayout>
      <section className="min-h-screen bg-black pt-6 md:pt-10 pb-10 md:pb-16">
        <div className="max-w-xl mx-auto px-5 md:px-6">
          {/* Title */}
          <h1 className="font-heading text-5xl md:text-7xl text-white leading-none text-center mb-[4.55rem] md:mb-[6.5rem]">
            Parlons de<br />votre projet
          </h1>

          {/* Description card */}
          <div className="bg-white rounded-2xl p-6 md:p-8 mb-6">
            <p className="font-avenir text-base md:text-lg leading-normal tracking-wide text-black text-left">
              Que vous ayez une idée très précise, quelques inspirations, ou que vous partiez d'une page blanche, Romuald prend le temps d'échanger avec vous.
            </p>
            <p className="font-avenir text-base md:text-lg leading-normal tracking-wide text-black text-left mt-6">
              Son regard, sa créativité et son sens du détail lui permettent de faire naître des idées, de concevoir des solutions et de donner vie à des réalisations uniques, parfois au-delà de ce que vous imaginez possible.
            </p>
          </div>

          {/* Section header: Contact */}
          <p className="text-white text-xs tracking-[0.25em] uppercase text-center py-4">
            Accueil et suivi de votre projet
          </p>

          {/* Contact info card */}
          <div className="bg-white rounded-2xl p-6 md:p-8 text-center mb-6">
            <p className="font-avenir text-lg md:text-xl text-black">L. Lobbardi</p>
            <a
              href="tel:0677453837"
              className="block font-avenir text-base md:text-lg text-black mt-0.5 cursor-pointer hover:opacity-70 transition-opacity"
            >
              06 77 45 38 37
            </a>
            <a
              href="mailto:projets@romudesigner.fr"
              className="block font-avenir text-base md:text-lg text-black mt-0.5 cursor-pointer hover:opacity-70 transition-opacity"
            >
              projets@romudesigner.fr
            </a>
          </div>

          {/* Section header: Form */}
          <p className="text-white text-xs tracking-[0.25em] uppercase text-center py-4">
            Ou présentez-nous votre projet
          </p>

          {/* Success state */}
          {status === 'success' ? (
            <div className="bg-white rounded-2xl p-8 md:p-10 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-black">
                <i className="ri-check-line text-2xl text-white"></i>
              </div>
              <h3 className="mt-6 font-heading text-2xl text-black">Merci pour votre message</h3>
              <p className="mt-3 text-black/60 font-avenir text-base">
                Nous avons bien reçu votre demande. Nous vous répondrons sous 48h.
              </p>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="mt-8 inline-block px-8 py-3 bg-black hover:bg-black/80 text-white text-sm rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            /* Form card */
            <form
              id="contact-form-romuald"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-6 md:p-8 flex flex-col gap-5 text-left"
            >
              <div className="flex flex-col gap-2">
                <label htmlFor="name" className="text-xs tracking-[0.2em] uppercase text-black/70 font-medium">
                  Nom et prénom
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="Votre nom et prénom"
                  className="border border-black/15 focus:border-black/40 rounded-md px-4 py-3 text-sm text-black placeholder:text-black/30 outline-none transition-colors bg-white"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-black/70 font-medium">
                  E-mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="Votre adresse e-mail"
                  className="border border-black/15 focus:border-black/40 rounded-md px-4 py-3 text-sm text-black placeholder:text-black/30 outline-none transition-colors bg-white"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="message" className="text-xs tracking-[0.2em] uppercase text-black/70 font-medium">
                  Votre message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Décrivez votre projet..."
                  className="border border-black/15 focus:border-black/40 rounded-md px-4 py-3 text-sm text-black placeholder:text-black/30 outline-none transition-colors resize-none bg-white"
                />
                <p className="text-xs text-black/40 text-right">500 caractères max.</p>
              </div>

              {/* Honeypot */}
              <div className="rd-hp-field" aria-hidden="true">
                <label htmlFor="website_alt">Ne pas remplir</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === 'error' && (
                <div className="p-4 rounded-md border border-black/20 bg-black/5 text-black/80 text-sm">
                  {formError || "Une erreur est survenue. Merci de réessayer."}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-1 w-full py-3.5 bg-black hover:bg-black/80 disabled:opacity-60 text-white text-sm tracking-[0.15em] uppercase rounded-md transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours…
                  </>
                ) : (
                  <>Envoyer ma demande</>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </PageLayout>
  );
}